import React, { useEffect, useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import type { EChartsOption } from 'echarts';
import { getCorporativoInfo } from '../../../lib/Util';
import { DateTime } from 'luxon';
import { t } from 'i18next';
import { OptionEmpresa } from '../../../shared/components/EmpresaSelector';

export interface AreaData {
  area: string;
  hours: number;
}

export interface CountryData {
  country: string;
  hours: number;
  cost: number;
}

export interface HoursDashboardProps {
  period: DateTime;
  areas: AreaData[];
  countries: CountryData[];
  company: OptionEmpresa;
}

type WorldGeoJSON = {
  type: string;
  features: unknown[];
};

export const COUNTRY_MAP: Record<string, string> = {
  peru: 'Peru',
  peruana: 'Peru',
  mexico: 'Mexico',
  brasil: 'Brazil',
  brasilia: 'Brazil',
  argentina: 'Argentina',
  chile: 'Chile',
  colombia: 'Colombia',
  ecuador: 'Ecuador',
  bolivia: 'Bolivia',
  paraguay: 'Paraguay',
  uruguay: 'Uruguay',
  venezuela: 'Venezuela',
  'costa rica': 'Costa Rica',
  panama: 'Panama',
  usa: 'United States of America',
  us: 'United States of America',
  unitedstates: 'United States of America',
  unitedstatesofamerica: 'United States of America',
  eeuu: 'United States of America',
  canada: 'Canada',
  'united states': 'United States of America',
  'south korea': 'Korea',
  'korea republic': 'Korea',
  'north korea': 'Dem. Rep. Korea',
  tanzania: 'Tanzania',
  'viet nam': 'Vietnam',
  'ivory coast': "Côte d'Ivoire",
  'czech republic': 'Czechia',
  slovakia: 'Slovakia',
  moldova: 'Moldova',
  brunei: 'Brunei',
  syria: 'Syria',
  alemania: 'Germany',
  germany: 'Germany',
  francia: 'France',
  france: 'France',
  espana: 'Spain',
  spain: 'Spain',
  italia: 'Italy',
  italy: 'Italy',
  portugal: 'Portugal',
  'reino unido': 'United Kingdom',
  unitedkingdom: 'United Kingdom',
  uk: 'United Kingdom',
  england: 'United Kingdom',
  rusia: 'Russia',
  russia: 'Russia',
  ucrania: 'Ukraine',
  ukraine: 'Ukraine',
  polonia: 'Poland',
  poland: 'Poland',
  holanda: 'Netherlands',
  netherlands: 'Netherlands',
  belgica: 'Belgium',
  suiza: 'Switzerland',
  suecia: 'Sweden',
  noruega: 'Norway',
  finlandia: 'Finland',
  dinamarca: 'Denmark',
  grecia: 'Greece',
  turquia: 'Turkey',
  china: 'China',
  japon: 'Japan',
  corea: 'South Korea',
  coreadelsur: 'South Korea',
  korea: 'South Korea',
  southkorea: 'South Korea',
  coreadelnorte: 'North Korea',
  northkorea: 'North Korea',
  india: 'India',
  indonesia: 'Indonesia',
  filipinas: 'Philippines',
  vietnam: 'Vietnam',
  tailandia: 'Thailand',
  malasia: 'Malaysia',
  singapur: 'Singapore',
  pakistan: 'Pakistan',
  iran: 'Iran',
  irak: 'Iraq',
  israel: 'Israel',
  arabiasaudi: 'Saudi Arabia',
  saudiarabia: 'Saudi Arabia',
  egipto: 'Egypt',
  marruecos: 'Morocco',
  nigeria: 'Nigeria',
  kenia: 'Kenya',
  etiopia: 'Ethiopia',
  sudafrica: 'South Africa',
  madagascar: 'Madagascar',
  australia: 'Australia',
  nuevazelanda: 'New Zealand',
};

const normalizeCountryKey = (country: string) => {
  return country
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ');
};

const formatCountryName = (country: string): string => {
  const normalized = normalizeCountryKey(country);

  return (
    COUNTRY_MAP[normalized] ??
    normalized
      .split(' ')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')
  );
};
export const HoursDashboardChart: React.FC<HoursDashboardProps> = ({ period, company, areas, countries }) => {
  const [worldGeoJSON, setWorldGeoJSON] = useState<WorldGeoJSON | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadMap = async () => {
      try {
        const res = await fetch('https://raw.githubusercontent.com/apache/echarts-www/master/asset/map/json/world.json');
        const json: WorldGeoJSON = await res.json();
        echarts.registerMap('world', json as any);
        if (isMounted) {
          setWorldGeoJSON(json);
        }
      } catch (err) {
        console.error('Error loading world map:', err);
      }
    };

    loadMap();

    return () => {
      isMounted = false;
    };
  }, []);

  const barOption: EChartsOption = useMemo(() => {
    return {
      title: {
        text: `${company.text} ${period.year} ${t('(Horas x Areas)')}`,
        left: 'center',
      },
      grid: {
        left: '5%',
        right: '5%',
        top: '15%',
        bottom: '5%',
        containLabel: true,
      },
      xAxis: {
        type: 'value',
      },
      yAxis: {
        type: 'category',
        data: areas.map((a) => a.area),
      },
      series: [
        {
          type: 'bar',
          data: areas.map((a) => a.hours),
          label: {
            show: true,
            position: 'right',
          },
          itemStyle: {
            color: '#2f6fed',
          },
        },
      ],
    };
  }, [areas, period]);

  const mapOption: EChartsOption = useMemo(() => {
    const maxHours = Math.max(...countries.map((c) => c.hours), 1);
    const countrySet = new Set(countries.map((c) => formatCountryName(c.country)));
    return {
      tooltip: {
        trigger: 'item',
        backgroundColor: 'rgba(10, 25, 45, 0.92)',
        borderColor: 'rgba(0, 120, 212, 0.25)',
        borderWidth: 1,
        textStyle: {
          color: '#ffffff',
          fontSize: 12,
        },
        extraCssText: 'border-radius: 12px; padding: 10px 12px; box-shadow: 0 10px 25px rgba(0,0,0,0.25);',
        formatter: (params: any) => {
          const data = params.data;

          return `
            <div style="
              font-size:13px;
              font-weight:700;
              color: rgba(199, 224, 244, 0.85);
              margin-bottom:6px;
              padding-bottom:6px;
              border-bottom: 1px solid rgba(199, 224, 244, 0.15);
            ">
              ${period.monthLong}
            </div>

            <div style="
              font-size:14px;
              font-weight:800;
              color:#ffffff;
              margin-bottom:10px;
            ">
              🌍 ${data?.name ?? params.name}
            </div>

            <div style="
              display:flex;
              justify-content:space-between;
              padding:4px 6px;
              border-radius:6px;
              background: rgba(0, 120, 212, 0.08); /* azul oscuro MUY suave */
              margin-bottom:6px;
            ">
              <span style="color: rgba(199, 224, 244, 0.75); margin-right: 10px;">${t('Horas')}:</span>
              <span style="color:rgba(199, 224, 244, 0.95); font-weight:600;">
                ${data?.hours ?? 0}
              </span>
            </div>

            <div style="
              display:flex;
              justify-content:space-between;
              padding:4px 6px;
              border-radius:6px;
              background: rgba(0, 120, 212, 0.05); /* aún más suave */
            ">
              <span style="color: rgba(199, 224, 244, 0.75); margin-right: 10px;">${t('Importe')}:</span>
              <span style="color: rgba(199, 224, 244, 0.95); font-weight:700;">
                ${data?.cost ?? 0}
              </span>
            </div>
          `;
        },
      },
      visualMap: {
        min: 0,
        max: maxHours,
        right: 'right',
        bottom: 'bottom',
        text: [t('Alto'), t('Bajo')],
        calculable: true,
      },
      series: [
        {
          name: 'Países',
          type: 'map',
          map: 'world',
          roam: true,
          label: {
            show: true,
            formatter: (params) => {
              return countrySet.has(params.name) ? params.name : '';
            },
            fontSize: 14,
          },

          emphasis: {
            label: {
              show: true,
              fontSize: 12,
              formatter: (params) => {
                return countrySet.has(params.name) ? params.name : '';
              },
            },
          },
          data: countries.map((c) => ({
            name: formatCountryName(c.country),
            value: c.hours,
            hours: c.hours,
            cost: c.cost,
          })),
        },
      ],
    };
  }, [countries]);

  if (!worldGeoJSON) {
    return (
      <div className="dashboard-empresas-grid">
        <div
          style={{
            borderRadius: 12,
            background: '#fff',
            padding: 16,
            border: '1px solid #edebe9',
            paddingLeft: 100,
          }}
        >
          <div
            style={{
              height: 24,
              width: '60%',
              background: '#f3f2f1',
              borderRadius: 6,
              marginBottom: 30,
            }}
          />

          {Array.from({ length: 12 }).map((_, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: 14,
              }}
            >
              <div
                style={{
                  width: 80,
                  height: 12,
                  background: '#f3f2f1',
                  borderRadius: 6,
                  marginRight: 12,
                }}
              />

              <div
                style={{
                  height: 20,
                  width: `${30 + index * 8}%`,
                  background: '#e1dfdd',
                  borderRadius: 8,
                }}
              />
            </div>
          ))}
        </div>

        <div
          style={{
            borderRadius: 12,
            background: '#fff',
            padding: 16,
            border: '1px solid #edebe9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              width: 350,
              height: 350,
              borderRadius: '50%',
              background: '#f3f2f1',
              position: 'relative',
            }}
          >
            <div
              style={{
                position: 'absolute',
                inset: 30,
                borderRadius: '50%',
                background: '#e1dfdd',
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-empresas-grid">
      <div style={{ borderRadius: 10, padding: 10 }}>
        <ReactECharts option={barOption} style={{ height: '100%', width: '100%' }} />
      </div>

      <div style={{ borderRadius: 10, padding: 10 }}>
        <ReactECharts option={mapOption} style={{ height: '100%', width: '100%' }} />
      </div>
    </div>
  );
};
